import type { BackgroundMapCustomOptions, BackgroundMapLayer, GuiTimeZone } from "@mm/metx-workbench.meteomatics.com";
import type mapboxgl from "mapbox-gl";
import {
  type Expression,
  type FilterSpecification,
  type GeoJSONSourceSpecification,
  type LinePaint,
  Point,
  type SymbolLayer,
} from "mapbox-gl";

import { type VoidAsyncResult, createResolvedAsyncResult } from "@/cache/AsyncResult";
import { networkCaches } from "@/cache/GlobalCache";
import { isCustomBackgroundMapStyle, resolveCustomBackgroundMapColors } from "@/constants/backgroundMapCustomStyle";
import { backgroundLayerConfig } from "@/constants/layerConfigAttributes";
import {
  Language,
  MaptilerBorderId,
  MaptilerEuropeFIRId,
  groundStyles,
  maptilerUrl,
  onlyBorderStyleLayer,
} from "@/constants/maptilerStyleOptions";
import { emptyFeatureCollection } from "@/geojson";
import { LayerBase } from "@/layers/LayerBase";
import Logger from "@mm/logging";
import type { DateTime } from "luxon";
import type { SceneLayerApi } from "../SceneLayerApi";

const logger = Logger.fromFilename(__filename);

function layerHasSource(v: unknown): v is { source: string } {
  if (!v || typeof v !== "object" || !Object.hasOwn(v, "source")) {
    return false;
  }
  return typeof (v as { source?: unknown }).source === "string";
}

type MapboxTransformPreloadAdapter = {
  center: unknown;
  centerPoint: Point;
  setLocationAtPoint(location: unknown, point: Point): void;
  _updateCameraOnTerrain?: () => void;
};

type SourceCachePreloadAdapter = {
  _preloadTiles?: (transform: MapboxTransformPreloadAdapter, callback: () => void) => void;
  _source?: unknown;
};

const backgroundPaintProperties: string[] = [
  "background-opacity",
  "fill-opacity",
  "line-opacity",
  "icon-opacity",
  "text-opacity",
  "raster-opacity",
  "circle-opacity",
  "fill-extrusion-opacity",
  "heatmap-opacity",
  "sky-opacity",

  "hillshade-exaggeration",
];

const layerIdCountryLines = "country-borders"; // https://cloud.maptiler.com/maps/3958f66e-04a7-48e9-a088-a3b3c617c617/
const layerIdEuroFirs = "euro firs"; // https://cloud.maptiler.com/maps/ff991184-9793-4129-9cc5-79bf690b8efb/
const BACKGROUND_PRELOAD_MIN_DRAG_DELTA_PX = 2;
const BACKGROUND_PRELOAD_THROTTLE_MS = 16;
const BACKGROUND_PRELOAD_MIN_LOOKAHEAD_PX = 256;
const BACKGROUND_PRELOAD_MAX_LOOKAHEAD_VIEWPORT_RATIO = 0.5;
const BACKGROUND_PRELOAD_LOOKAHEAD_MULTIPLIER = 20;
const layerIdBackground = "background";
const layerIdWater = "water";
const layerIdAdminCountry = "admin_country";

export class BackgroundMapLayerImpl extends LayerBase<BackgroundMapLayer> {
  abortController: AbortController | undefined;

  sources: mapboxgl.Sources = {};
  layers: mapboxgl.AnyLayer[] = [];
  private firstLayer = "";
  private lastPanInteractionPoint: { x: number; y: number } | null = null;
  private lastPanPreloadAt = Number.NEGATIVE_INFINITY;

  constructor(id: number, props: BackgroundMapLayer, scene: SceneLayerApi, timezone: GuiTimeZone) {
    super(id, props, scene, timezone);
    this.addPlaceholderLayer();
    this.updateStyle(props.style);
    this.bindPanPreloadListeners();
    // TODO: why is this here?
    //this.invalidate(InvalidationType.ALL);
  }

  get mapboxIndex(): string {
    return this.firstLayer;
  }

  moveZIndex(beforeLayerId?: string) {
    const map = this.scene.getMapboxMap();
    map.moveLayer(this.getPlaceholderId(), beforeLayerId);
    for (const layer of this.layers) {
      const layerId = this.getSourceId(layer.id);
      if (map.getLayer(layerId)) {
        map.moveLayer(layerId, this.getPlaceholderId());
      }
    }
  }

  getActiveWeatherParametersAsString() {
    return [];
  }

  getActiveWeatherParameters() {
    return [];
  }

  isRebuffering(_: DateTime): boolean {
    // Check all the source consisting of the background layer in order to conclude
    // if it is rebuffering.
    Object.keys(this.sources).reduce((sourceRebuffering, key) => {
      const sourceId = this.getSourceId(key);
      const currSourceRebuffering = !this.scene.getMapboxMap().isSourceLoaded(sourceId);
      // If at least one source is rebuffering, we consider the background is still loading.
      return sourceRebuffering || currSourceRebuffering;
    }, false);
    return false;
  }

  private updateBorderLayerFilter(style: string, customOptions: BackgroundMapCustomOptions) {
    if (style === MaptilerBorderId) {
      const showValue = !!customOptions.show_state_border;
      this.updateFilter(style, layerIdCountryLines, showStateBorderExpression(showValue));
    }
  }
  private updateBorderPaintProperties(styleId: string, customOptions: BackgroundMapCustomOptions) {
    // border line-width
    if (styleId === MaptilerBorderId) {
      this.updateMapboxLinePaintProperties(layerIdCountryLines, {
        "line-width": BorderLineWithExpression,
      });
      this.updateMapboxLinePaintProperties(layerIdCountryLines, {
        "line-color": customOptions.line_color ?? backgroundLayerConfig.defaultLineColor,
      });
    }
    // border line-color
    if (styleId === MaptilerEuropeFIRId) {
      this.updateMapboxLinePaintProperties(layerIdEuroFirs, {
        "line-color": customOptions.line_color ?? backgroundLayerConfig.defaultLineColor,
      });
    }
  }
  private updateMapLabelLanguageProperty(styleId: string, customOptions: BackgroundMapCustomOptions) {
    const languageStr = customOptions?.map_label_language ? customOptions?.map_label_language : Language.EN;

    // we don't want to update label for border style layers
    // additional MaptilerSaBorderId has text with other fields then (name, "name:" + languageStr)
    if (!onlyBorderStyleLayer.includes(styleId)) {
      let nameLang = "name";
      if (languageStr !== Language.NATIVE) {
        nameLang = `name:${languageStr}`;
      }
      for (const layer of this.layers) {
        try {
          // only change text-field when source layer is a place
          // @ts-ignore
          if (layer["source-layer"] && layer["source-layer"] === "place") {
            this.scene.getMapboxMap().setLayoutProperty(this.getSourceId(layer.id), "text-field", ["get", nameLang]);
          }
        } catch (e) {
          // No warning console.warn(e);
        }
      }
    }
  }

  updateMapboxPaintProperties(layerId: string, props?: Partial<mapboxgl.PaintSpecification>) {
    if (this.scene.getMapboxMap().getLayer(this.getSourceId(layerId))) {
      const entries = Object.entries(props ?? {}) as Array<
        [keyof mapboxgl.PaintSpecification, mapboxgl.PaintSpecification[keyof mapboxgl.PaintSpecification]]
      >;
      for (const [name, value] of entries) {
        try {
          this.scene.getMapboxMap().setPaintProperty(this.getSourceId(layerId), name, value);
        } catch {
          // No warning console.warn(e);
        }
      }
    }
  }

  updateMapboxLinePaintProperties(layerId: string, props?: LinePaint) {
    this.updateMapboxPaintProperties(layerId, props);
  }

  private updateCustomBackgroundPaintProperties(styleId: string, customOptions: BackgroundMapCustomOptions) {
    if (!isCustomBackgroundMapStyle(styleId)) {
      return;
    }

    const colors = resolveCustomBackgroundMapColors(customOptions);
    this.updateMapboxPaintProperties(layerIdBackground, {
      "background-color": colors.backgroundColor,
    });
    this.updateMapboxPaintProperties(layerIdWater, {
      "fill-color": colors.waterColor,
    });
    this.updateMapboxPaintProperties(layerIdAdminCountry, {
      "line-color": colors.borderColor,
    });
  }

  updateFilter(
    maptilerId: string,
    layerId: string,
    filter?: FilterSpecification | null,
    options?: Parameters<mapboxgl.Map["setFilter"]>[2] | null,
  ) {
    if (this.scene.getMapboxMap().getLayer(this.getSourceId(layerId))) {
      this.scene.getMapboxMap().setFilter(this.getSourceId(layerId), filter, options ?? undefined);
    }
  }

  updateOpacity(opacity: number) {
    for (const layer of this.layers) {
      for (const property of backgroundPaintProperties) {
        try {
          const name = property as keyof mapboxgl.PaintSpecification;
          const value = opacity as mapboxgl.PaintSpecification[typeof name];
          this.scene.getMapboxMap().setPaintProperty(this.getSourceId(layer.id), name, value);
        } catch (_e) {
          // No warning console.warn(e);
        }
      }
    }
  }

  updateShow(show: boolean) {
    const map = this.scene.getMapboxMap();
    for (const layer of this.layers) {
      const layerId = this.getSourceId(layer.id);
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, "visibility", show ? "visible" : "none");
      }
    }
  }

  releaseLayer() {
    const map = this.scene.getMapboxMap();
    map.off("movestart", this.onMapMoveStart);
    map.off("move", this.onMapMove);
    map.off("moveend", this.onMapMoveEnd);
    this.lastPanInteractionPoint = null;
    this.lastPanPreloadAt = Number.NEGATIVE_INFINITY;
  }

  checker() {
    return {
      opacity: (prev: BackgroundMapLayer, curr: BackgroundMapLayer) => {
        if (prev.opacity !== curr.opacity) {
          this.setLayerProps({ opacity: curr.opacity });
          // only allow opacity on layer with border
          if (onlyBorderStyleLayer.includes(this.props.style)) {
            this.updateOpacity(this.props.opacity);
          }
        }
        return false; // internally managed change detection by mapbox
      },
      style: (prev: BackgroundMapLayer, curr: BackgroundMapLayer) => {
        if (prev.style !== curr.style) {
          this.setLayerProps({ style: curr.style });
          this.updateStyle(curr.style);
        }
        return false; // internally managed change detection by mapbox
      },
      show: (prev: BackgroundMapLayer, curr: BackgroundMapLayer) => {
        if (prev.show !== curr.show) {
          this.setLayerProps({ show: curr.show });
          this.updateShow(curr.show);
        }

        return false; // internally managed change detection by mapbox
      },
      index: (prev: BackgroundMapLayer, curr: BackgroundMapLayer) => {
        if (prev.index !== curr.index) {
          this.setLayerProps({ index: curr.index });
        }
        return false; // internally managed change detection by mapbox
      },
      calibrated: () => false,
      custom_options: (prev: BackgroundMapLayer, curr: BackgroundMapLayer) => {
        if (prev.custom_options !== curr.custom_options) {
          this.setLayerProps({ custom_options: curr.custom_options });
          this.updateBorderLayerFilter(curr.style, curr.custom_options);
          // set style for line with paint properties of the border
          this.updateBorderPaintProperties(curr.style, curr.custom_options);
          this.updateCustomBackgroundPaintProperties(curr.style, curr.custom_options);
          this.updateMapLabelLanguageProperty(curr.style, curr.custom_options);
        }
        return false;
      },
      vertical_interpolation: () => false,
      experimental: () => false,
    };
  }

  /**
   * Update the background layer by removing the whole mapbox state and rebuilding it from scratch.
   */
  private updateStyle(newStyle: string) {
    this.removeLayer();
    this.addPlaceholderLayer();
    this.fetchMaptilerStyle(newStyle).then((style) => {
      // if the style is null, this means that the first of two rapid style transitions was cancelled.
      // just keep the old style until the second transition resolves.
      if (style != null) {
        this.setStyle(style).then(() => {
          // only allow opacity on layer with border
          if (onlyBorderStyleLayer.includes(newStyle)) {
            this.updateOpacity(this.props.opacity);
          }
          this.updateShow(this.props.show);
          // set filter of country border level
          this.updateBorderLayerFilter(newStyle, this.props.custom_options);
          // set style for line with paint properties of the border
          this.updateBorderPaintProperties(newStyle, this.props.custom_options);
          this.updateCustomBackgroundPaintProperties(newStyle, this.props.custom_options);
          this.updateMapLabelLanguageProperty(newStyle, this.props.custom_options);
        });
      }
    });
  }

  private getSourceId(key: string): string {
    return `metx.background#${key}#source${this.uid}`;
  }

  private getPlaceholderId(): string {
    return `metx.background#placeholder#${this.uid}`;
  }

  // TODO: this should be a generalized method
  // TODO: how is this cached?
  protected fetchMaptilerStyle(style: string): Promise<mapboxgl.Style | null> {
    const groundStyle = groundStyles.find((groundStyle) => groundStyle.id === style);
    const url = groundStyle ? groundStyle.uri : maptilerUrl(style);

    if (this.abortController?.signal.aborted) {
      this.abortController = new AbortController();
    } else {
      this.abortController?.abort();
      this.abortController = new AbortController();
    }
    const signal = this.abortController.signal;
    const myRequest = new Request(url);
    return fetch(myRequest, {
      signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP status ${response.status}`);
        }
        return response.json(); // TODO: validate
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          throw err;
        }
        return null;
      });
  }

  private async setStyle(data: mapboxgl.Style) {
    if (data.sprite) {
      // @ts-ignore Mapbox typings are bad because it's flow project. Id is always there.
      const id = data.id;
      const { asynchronous } = networkCaches.maptiler_sprite_cache.loadSpriteSheet(id, window.devicePixelRatio);

      const icons = await asynchronous;
      if (icons.length) {
        const map = this.scene.getMapboxMap();
        for (const icon of icons) {
          if (map.hasImage(icon.name)) {
            continue;
          }
          map.addImage(icon.name, icon.data, {
            pixelRatio: icon.pixelRation,
            sdf: icon.sdf,
          });
        }
      }
    }

    if (data.sources) {
      this.sources = data.sources;
      // biome-ignore lint/complexity/noForEach: Sources do not have value
      Object.entries(this.sources).forEach(([key, value]) => {
        this.scene.getMapboxMap().addSource(this.getSourceId(key), value);
      });
    }
    if (data.layers?.length) {
      this.layers = data.layers;
      this.layers.forEach((layer, idx) => {
        let newLayer: mapboxgl.AnyLayer;

        if (layerHasSource(layer)) {
          const sourceLayer = {
            ...layer,
            id: this.getSourceId(layer.id),
            source: this.getSourceId(layer.source),
          };
          newLayer = sourceLayer;
        } else {
          newLayer = { ...layer, id: this.getSourceId(layer.id) };
        }
        if (idx === 0) {
          this.firstLayer = newLayer.id;
        }
        this.scene.getMapboxMap().addLayer(newLayer, this.getPlaceholderId());
      });
    }
  }

  private addPlaceholderLayer() {
    if (this.scene.getMapboxMap().getLayer(this.getPlaceholderId())) {
      return;
    }
    const source: GeoJSONSourceSpecification = {
      type: "geojson",
      data: emptyFeatureCollection,
    };
    const placeholderLayer: SymbolLayer = {
      id: this.getPlaceholderId(),
      type: "symbol",
      source: this.getPlaceholderId(),
    };
    this.firstLayer = placeholderLayer.id;
    this.scene.getMapboxMap().addSource(this.getPlaceholderId(), source);
    this.scene.getMapboxMap().addLayer(placeholderLayer);
  }

  private bindPanPreloadListeners() {
    const map = this.scene.getMapboxMap();
    map.on("movestart", this.onMapMoveStart);
    map.on("move", this.onMapMove);
    map.on("moveend", this.onMapMoveEnd);
  }

  private readonly onMapMoveStart = (event: { originalEvent?: Event }) => {
    this.lastPanInteractionPoint = this.extractInteractionPoint(event.originalEvent);
    this.lastPanPreloadAt = Number.NEGATIVE_INFINITY;
  };

  private readonly onMapMove = (event: { originalEvent?: Event }) => {
    if (!this.props.show || Object.keys(this.sources).length === 0) {
      return;
    }

    const point = this.extractInteractionPoint(event.originalEvent);
    if (!point) {
      return;
    }

    if (!this.lastPanInteractionPoint) {
      this.lastPanInteractionPoint = point;
      return;
    }

    const dx = point.x - this.lastPanInteractionPoint.x;
    const dy = point.y - this.lastPanInteractionPoint.y;
    this.lastPanInteractionPoint = point;

    if (Math.hypot(dx, dy) < BACKGROUND_PRELOAD_MIN_DRAG_DELTA_PX) {
      return;
    }

    const now = performance.now();
    if (now - this.lastPanPreloadAt < BACKGROUND_PRELOAD_THROTTLE_MS) {
      return;
    }
    this.lastPanPreloadAt = now;

    this.preloadTilesInPanDirection(dx, dy);
  };

  private readonly onMapMoveEnd = () => {
    this.lastPanInteractionPoint = null;
    this.lastPanPreloadAt = Number.NEGATIVE_INFINITY;
  };

  private extractInteractionPoint(event?: Event): { x: number; y: number } | null {
    if (!event) {
      return null;
    }

    if (event instanceof MouseEvent) {
      return { x: event.clientX, y: event.clientY };
    }

    if (typeof TouchEvent !== "undefined" && event instanceof TouchEvent) {
      const touch = event.touches[0] ?? event.changedTouches[0];
      if (!touch) {
        return null;
      }
      return { x: touch.clientX, y: touch.clientY };
    }

    return null;
  }

  private preloadTilesInPanDirection(dx: number, dy: number) {
    const map = this.scene.getMapboxMap() as mapboxgl.Map & {
      transform?: {
        clone?: () => MapboxTransformPreloadAdapter;
      };
      style?: {
        getSourceCaches?: (sourceId?: string | null) => SourceCachePreloadAdapter[];
      };
    };

    const transform = map.transform?.clone?.();
    if (!transform) {
      return;
    }

    const canvas = map.getCanvas();
    const offsetX = this.computeLookaheadOffset(dx, canvas.clientWidth);
    const offsetY = this.computeLookaheadOffset(dy, canvas.clientHeight);
    if (offsetX === 0 && offsetY === 0) {
      return;
    }

    try {
      const pointAtOffset = transform.centerPoint.add(new Point(offsetX, offsetY));
      transform.setLocationAtPoint(transform.center, pointAtOffset);
      transform._updateCameraOnTerrain?.();
      let didPreload = false;

      for (const [key, sourceSpec] of Object.entries(this.sources)) {
        if (!this.isTilePreloadableSource(sourceSpec)) {
          continue;
        }

        const sourceId = this.getSourceId(key);
        if (!map.getSource(sourceId) || !map.isSourceLoaded(sourceId)) {
          continue;
        }

        const sourceCaches = map.style?.getSourceCaches?.(sourceId) ?? [];
        for (const sourceCache of sourceCaches) {
          if (!this.isSourceCacheReadyForPreload(sourceCache, sourceSpec)) {
            continue;
          }

          try {
            if (sourceCache._preloadTiles) {
              sourceCache._preloadTiles(transform, () => {});
              didPreload = true;
            }
          } catch (error) {
            if (this.isMissingTileTemplatePreloadError(error)) {
              continue;
            }
            throw error;
          }
        }
      }

      if (didPreload) {
        map.triggerRepaint();
      }
    } catch (error) {
      logger.warn("Background tile preloading failed; continuing without pan preload.", error);
    }
  }

  private computeLookaheadOffset(delta: number, viewportSize: number): number {
    if (delta === 0 || viewportSize <= 0) {
      return 0;
    }

    const maxLookahead = Math.round(viewportSize * BACKGROUND_PRELOAD_MAX_LOOKAHEAD_VIEWPORT_RATIO);
    const signedOffset = Math.round(delta * BACKGROUND_PRELOAD_LOOKAHEAD_MULTIPLIER);
    const clampedOffset = Math.max(-maxLookahead, Math.min(signedOffset, maxLookahead));
    const minLookahead = Math.min(BACKGROUND_PRELOAD_MIN_LOOKAHEAD_PX, maxLookahead);

    if (Math.abs(clampedOffset) >= minLookahead) {
      return clampedOffset;
    }

    return Math.sign(clampedOffset || delta) * minLookahead;
  }

  private isTilePreloadableSource(
    source: mapboxgl.AnySourceData | undefined,
  ): source is mapboxgl.VectorSourceSpecification | mapboxgl.RasterSourceSpecification {
    return source?.type === "vector" || source?.type === "raster";
  }

  private isSourceCacheReadyForPreload(
    sourceCache: unknown,
    sourceSpec: mapboxgl.VectorSourceSpecification | mapboxgl.RasterSourceSpecification,
  ): sourceCache is SourceCachePreloadAdapter {
    if (typeof this.getUnknownProperty(sourceCache, "_preloadTiles") !== "function") {
      return false;
    }

    const preloadableSourceCache = sourceCache as SourceCachePreloadAdapter;
    return (
      this.hasConcreteTileTemplates(sourceSpec) ||
      this.hasConcreteTileTemplates(preloadableSourceCache._source) ||
      this.hasConcreteTileTemplates(this.getUnknownProperty(preloadableSourceCache._source, "_options")) ||
      this.hasConcreteTileTemplates(this.getUnknownProperty(preloadableSourceCache._source, "tileJSON"))
    );
  }

  private hasConcreteTileTemplates(source: unknown): boolean {
    const tiles = this.getUnknownProperty(source, "tiles");
    return Array.isArray(tiles) && tiles.some((tile) => typeof tile === "string" && tile.length > 0);
  }

  private getUnknownProperty(source: unknown, key: string): unknown {
    if (!source || typeof source !== "object" || !(key in source)) {
      return undefined;
    }

    return (source as Record<string, unknown>)[key];
  }

  private isMissingTileTemplatePreloadError(error: unknown): boolean {
    return error instanceof TypeError && error.message.includes("replace");
  }

  removeLayer() {
    this.removePlaceholderLayer();
    this.removeStyleLayers();
  }

  private removePlaceholderLayer() {
    this.scene.getMapboxMap().removeLayer(this.getPlaceholderId());
    this.scene.getMapboxMap().removeSource(this.getPlaceholderId());
  }

  private removeStyleLayers(): void {
    for (const layer of this.layers) {
      const id = this.getSourceId(layer.id);
      if (this.scene.getMapboxMap().getLayer(id)) {
        this.scene.getMapboxMap().removeLayer(id);
      } else {
        logger.error(`No Layer with Id: ${id}`);
      }
    }
    this.layers = [];
    for (const entry of Object.entries(this.sources)) {
      const id = this.getSourceId(entry[0]);
      if (this.scene.getMapboxMap().getSource(id)) {
        this.scene.getMapboxMap().removeSource(id);
      } else {
        logger.error(`No Source with Id: ${id}`);
      }
    }
    this.sources = {};
  }

  beforeRender(): void {
    // Empty since mapbox calls the render function separately.
  }

  // No prefetching
  fetchData(_timeFrame: DateTime): VoidAsyncResult {
    return createResolvedAsyncResult(undefined);
  }
}

const BorderLineWithExpression: number | Expression | undefined = [
  "case",
  ["==", ["get", "level"], 1],
  1,
  ["==", ["get", "level"], 0],
  2,
  2,
];

const showStateBorderExpression = (showStateBorder: boolean): FilterSpecification => {
  if (showStateBorder) {
    return ["any", ["<=", "level", 1]];
  }
  return ["any", ["==", "level", 0]];
};
